import classnames from "classnames";
import React from "react";
import { useSelector } from "react-redux";

import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import RenderCallback from "@utils/renderCallback";
import { SearchComponent } from "@utils/search/@types/searchComponent";

import * as ScrollPositionService from "@services/scrollPosition.service";

import {
    hasAnyComponents,
    hasResults,
    hasSelectedFacets,
} from "@redux/modules/value/search/search.selectors";

import { NumberOfHits } from "@components/Search/SearchResults/StateComponents/NumberOfHits/numberOfHits.container";
import { ContentSearchResultsComponent } from "@components/Search/SearchResults/contentSearchResults.component";
import { ProductSearchResultsComponent } from "@components/Search/SearchResults/productSearchResults.component";

import { SearchTitle } from "./components/SearchTitle/searchTitle.container";
import "./search.scss";
import { SearchResultProductTileConfig } from "./searchResults.container";

type SearchResultsComponentProps = {
    productTileConfig?: SearchResultProductTileConfig;
    showFacets: boolean;
};

const SearchResultsComponent = ({
    productTileConfig,
    showFacets,
}: SearchResultsComponentProps) => {
    const onSearchPage = isPageTemplate(PageTemplates.SEARCH);

    const hasResultComponents = useSelector(
        hasAnyComponents([
            SearchComponent.PRODUCT_NUMBER_OF_HITS,
            SearchComponent.CONTENT_NUMBER_OF_HITS,
        ]),
    );

    const hasSelectedProductFacets = useSelector(
        hasSelectedFacets(SearchComponent.PRODUCT_FACETS),
    );

    const hasProductResults = useSelector(
        hasResults(SearchComponent.PRODUCT_NUMBER_OF_HITS),
    );
    const hasContentResults = useSelector(
        hasResults(SearchComponent.CONTENT_NUMBER_OF_HITS),
    );

    const afterRender = () => {
        ScrollPositionService.scrollToLastPosition();
    };

    const hasAnyResults = hasProductResults || hasContentResults;

    const renderProductResults = hasProductResults || hasSelectedProductFacets;
    const renderContentResults = hasContentResults && onSearchPage;

    if (!hasResultComponents || !hasAnyResults) return null;

    return (
        <article className={"search__wrapper"}>
            {onSearchPage && <SearchTitle />}
            {showFacets && <NumberOfHits />}
            <section
                className={classnames("search", {
                    "search--no-facets": !showFacets,
                })}
            >
                <RenderCallback callback={afterRender}>
                    {renderProductResults && (
                        <ProductSearchResultsComponent
                            productTileConfig={productTileConfig}
                            showFacets={showFacets}
                        />
                    )}
                    {renderContentResults && (
                        <ContentSearchResultsComponent
                            expanded={!renderProductResults}
                        />
                    )}
                </RenderCallback>
            </section>
        </article>
    );
};

export { SearchResultsComponent };
